import React, { useCallback, useRef, useMemo, useImperativeHandle, forwardRef } from 'react';
import { AgGridReact, type AgGridReactProps } from 'ag-grid-react';
import {
  type ColDef,
  type ColumnResizedEvent,
  type SortChangedEvent,
  type ColumnMovedEvent,
  type GridReadyEvent,
  type GridSizeChangedEvent,
  type LocaleText,
} from 'ag-grid-community';
import { useTheme } from '../../contexts/ThemeContext';
import { agThemeLight, agThemeDark } from '../../utils/agGridTheme';

interface SavedColumnState {
  colId: string;
  width?: number;
  flex?: number;
  sort?: 'asc' | 'desc' | null;
  sortIndex?: number | null;
}

function gridHasFlexColumns(api: GridReadyEvent['api']): boolean {
  return (
    api.getColumns()?.some((col) => {
      const flex = col.getColDef().flex;
      return flex != null && flex > 0;
    }) ?? false
  );
}

/** Flex kolonlar viewport'u doldurmalı; resize sonrası boşluk kalırsa dağıt. */
function rebalanceFlexColumnsToFillWidth(api: GridReadyEvent['api'], clientWidth: number) {
  // Görünür kolonlar — hidden olanlar toplam genişliği şişirir, gap hesabını bozar.
  const displayedColumns = api.getAllDisplayedColumns();
  if (!displayedColumns?.length || clientWidth <= 0) return;

  const gridBodyWidth = clientWidth;

  const totalWidth = displayedColumns.reduce((sum, col) => sum + col.getActualWidth(), 0);
  const gap = gridBodyWidth - totalWidth;
  if (gap <= 1) return;

  const columnState = api.getColumnState();
  const activeFlexCols = displayedColumns.filter((col) => {
    const state = columnState.find((s) => s.colId === col.getColId());
    return state?.flex != null && state.flex > 0;
  });

  const flexDefCols = displayedColumns.filter((col) => (col.getColDef().flex ?? 0) > 0);
  const candidates = activeFlexCols.length > 0 ? activeFlexCols : flexDefCols;

  if (candidates.length === 0) {
    // Hiç flex yoksa son görünür kolonu genişleterek boşluğu kapat — kullanıcının
    // manuel genişlettiği diğer kolonlara dokunma.
    const last = displayedColumns[displayedColumns.length - 1];
    if (last) api.setColumnWidths([{ key: last, newWidth: last.getActualWidth() + gap }]);
    return;
  }

  const target = candidates.reduce((best, col) =>
    (col.getColDef().flex ?? 0) >= (best.getColDef().flex ?? 0) ? col : best,
  );
  api.setColumnWidths([{ key: target, newWidth: target.getActualWidth() + gap }]);
}

function saveGridState(key: string, columns: SavedColumnState[]) {
  try {
    localStorage.setItem(key, JSON.stringify(columns));
  } catch { /* quota exceeded */ }
}

function loadGridState(key: string): SavedColumnState[] | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export interface BaseGridProps<TData = any> extends Omit<
  AgGridReactProps<TData>,
  'theme' | 'animateRows' | 'suppressCellFocus' | 'tooltipShowDelay' | 'onGridReady' | 'onColumnResized' | 'onSortChanged' | 'onColumnMoved' | 'onGridSizeChanged'
> {
  gridStateKey?: string;
  onGridReady?: (event: GridReadyEvent) => void;
  onColumnResized?: (event: ColumnResizedEvent) => void;
  onSortChanged?: (event: SortChangedEvent) => void;
  onColumnMoved?: (event: ColumnMovedEvent) => void;
  onGridSizeChanged?: (event: GridSizeChangedEvent) => void;
}

const DEFAULT_COL_DEF: ColDef = {
  sortable: true,
  resizable: true,
  filter: true,
  suppressMovable: false,
};

/** Ortak boş grid / filtre sonucu metinleri (tüm BaseGrid kullanımları). */
const DEFAULT_LOCALE_TEXT: LocaleText = {
  noRowsToShow: 'Gösterilecek kayıt bulunmuyor',
  noMatchingRows: 'Gösterilecek kayıt bulunmuyor',
};

function BaseGridInner<TData = any>(
  props: BaseGridProps<TData>,
  ref: React.ForwardedRef<AgGridReact<TData>>
) {
  const {
    gridStateKey,
    defaultColDef: externalDefaultColDef,
    localeText: externalLocaleText,
    onGridReady: externalOnGridReady,
    onColumnResized: externalOnColumnResized,
    onSortChanged: externalOnSortChanged,
    onColumnMoved: externalOnColumnMoved,
    onGridSizeChanged: externalOnGridSizeChanged,
    ...rest
  } = props;

  const { isDarkMode } = useTheme();
  const internalRef = useRef<AgGridReact<TData>>(null);
  /** Kayıtlı kolon genişliği varsa sizeColumnsToFit ile ezme */
  const hasPersistedWidthsRef = useRef(false);
  /** İlk viewport fit'i yalnızca bir kez uygula (çift resize önleme) */
  const initialFitAppliedRef = useRef(false);

  useImperativeHandle(ref, () => internalRef.current!, []);

  const mergedDefaultColDef = useMemo<ColDef>(
    () => (externalDefaultColDef ? { ...DEFAULT_COL_DEF, ...externalDefaultColDef } : DEFAULT_COL_DEF),
    [externalDefaultColDef]
  );

  const localeText = useMemo<LocaleText>(
    () => ({ ...DEFAULT_LOCALE_TEXT, ...externalLocaleText }),
    [externalLocaleText]
  );

  const getGridClientWidth = useCallback((): number => {
    const gridDiv = (internalRef.current as unknown as { eGridDiv?: HTMLElement })?.eGridDiv;
    return gridDiv?.clientWidth ?? 0;
  }, []);

  const fitColumnsToViewport = useCallback((api: GridReadyEvent['api']) => {
    const allColumns = api.getColumns();
    if (!allColumns || allColumns.length === 0) return;
    api.sizeColumnsToFit();
  }, []);

  const persistState = useCallback(() => {
    if (!gridStateKey) return;
    const api = internalRef.current?.api;
    if (!api) return;
    const cols = api.getColumns();
    if (!cols) return;
    const state: SavedColumnState[] = cols.map((col) => {
      const def = col.getColDef();
      const entry: SavedColumnState = {
        colId: col.getColId(),
        sort: col.getSort() ?? null,
        sortIndex: col.getSortIndex() ?? null,
      };
      if (def.flex != null && def.flex > 0) {
        entry.flex = def.flex;
      } else {
        entry.width = col.getActualWidth();
      }
      return entry;
    });
    saveGridState(gridStateKey, state);
  }, [gridStateKey]);

  const handleGridReady = useCallback((event: GridReadyEvent) => {
    const saved = gridStateKey ? loadGridState(gridStateKey) : null;
    const usesFlex = gridHasFlexColumns(event.api);

    if (saved?.length) {
      const colState = saved.map((s) => {
        const col = event.api.getColumn(s.colId);
        const colDef = col?.getColDef();
        const colDefFlex = colDef?.flex;
        if (colDefFlex != null && colDefFlex > 0) {
          return {
            colId: s.colId,
            flex: s.flex ?? colDefFlex,
            ...(s.sort ? { sort: s.sort, sortIndex: s.sortIndex } : {}),
          };
        }
        if (colDef?.width != null && colDef.resizable === false) {
          return {
            colId: s.colId,
            width: colDef.width,
            flex: null,
            ...(s.sort ? { sort: s.sort, sortIndex: s.sortIndex } : {}),
          };
        }
        return {
          colId: s.colId,
          ...(s.width ? { width: s.width, flex: null } : {}),
          ...(s.sort ? { sort: s.sort, sortIndex: s.sortIndex } : {}),
        };
      });
      event.api.applyColumnState({ state: colState, defaultState: { sort: null } });
      hasPersistedWidthsRef.current = saved.some((s) => {
        if (!s.width) return false;
        const col = event.api.getColumn(s.colId);
        const flex = col?.getColDef()?.flex;
        return !(flex != null && flex > 0);
      });
    }

    if (usesFlex) {
      initialFitAppliedRef.current = true;
      requestAnimationFrame(() => rebalanceFlexColumnsToFillWidth(event.api, getGridClientWidth()));
    }

    externalOnGridReady?.(event);
  }, [gridStateKey, externalOnGridReady, getGridClientWidth]);

  const handleColumnResized = useCallback((e: ColumnResizedEvent) => {
    if (e.finished) {
      if (gridHasFlexColumns(e.api)) {
        rebalanceFlexColumnsToFillWidth(e.api, getGridClientWidth());
      }
      persistState();
    }
    externalOnColumnResized?.(e);
  }, [persistState, externalOnColumnResized, getGridClientWidth]);

  const handleSortChanged = useCallback((e: SortChangedEvent) => {
    persistState();
    externalOnSortChanged?.(e);
  }, [persistState, externalOnSortChanged]);

  const handleColumnMoved = useCallback((e: ColumnMovedEvent) => {
    if (e.finished) persistState();
    externalOnColumnMoved?.(e);
  }, [persistState, externalOnColumnMoved]);

  const handleGridSizeChanged = useCallback((e: GridSizeChangedEvent) => {
    const usesFlex = gridHasFlexColumns(e.api);
    if (usesFlex) {
      rebalanceFlexColumnsToFillWidth(e.api, e.clientWidth);
      initialFitAppliedRef.current = true;
    } else if (!hasPersistedWidthsRef.current && !initialFitAppliedRef.current) {
      fitColumnsToViewport(e.api);
      initialFitAppliedRef.current = true;
    }
    externalOnGridSizeChanged?.(e);
  }, [externalOnGridSizeChanged, fitColumnsToViewport]);

  // Kolon görünürlüğü değişince (chooser ya da kolon menüsü) boşluğu kapat.
  const handleDisplayedColumnsChanged = useCallback(() => {
    const api = internalRef.current?.api;
    if (!api) return;
    rebalanceFlexColumnsToFillWidth(api, getGridClientWidth());
  }, [getGridClientWidth]);

  return (
    <AgGridReact<TData>
      ref={internalRef}
      theme={isDarkMode ? agThemeDark : agThemeLight}
      defaultColDef={mergedDefaultColDef}
      localeText={localeText}
      domLayout="autoHeight"
      enableCellTextSelection={true}
      ensureDomOrder={true}
      animateRows={true}
      suppressCellFocus={true}
      tooltipShowDelay={300}
      onGridReady={handleGridReady}
      onColumnResized={handleColumnResized}
      onSortChanged={handleSortChanged}
      onColumnMoved={handleColumnMoved}
      onGridSizeChanged={handleGridSizeChanged}
      onDisplayedColumnsChanged={handleDisplayedColumnsChanged}
      {...rest}
    />
  );
}

const BaseGrid = forwardRef(BaseGridInner) as <TData = any>(
  props: BaseGridProps<TData> & { ref?: React.Ref<AgGridReact<TData>> }
) => React.ReactElement | null;

export default BaseGrid;
